import { CheckCircleOutlined, ClockCircleOutlined, DeleteOutlined, ExclamationCircleOutlined, WarningOutlined } from '@ant-design/icons';
import { App, Button, message } from 'antd';
import { arrayOf, func, shape, string } from 'prop-types';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useEffect } from 'react';

// Internal components and hooks
import { SosSection } from './components';
import { donatePopupFields, SosItem } from '@/components/atoms';
import { PageError, PageHeader, PageLoading } from '@/components/organisms';
import { Layout } from '@/components/templates';
import { SosStatus, useSosNewContainerCheckoutSosMutation } from '@/graphql';

// Assets
import SosIcon from '@/assets/icons/sos.png';

const SosMySosLayout = ({ children, onBack }) => (
    <>
        <PageHeader icon={{ src: SosIcon }} onBack={onBack} title="My SOS" />
        <Layout>{children}</Layout>
    </>
);

const SosMySosData = ({ data, onBack, onDelete, onPaid, onRequestRefund }) => {
    const { modal } = App.useApp();
    const { search } = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        const urlParams = new URLSearchParams(search);
        const params = {};
        for (const [key, value] of urlParams.entries()) {
            params[key] = value;
        }
        if (params.type === 'success') {
            saveSession(params);
        }
        if (params.type === 'cancel') {
            modal.warning({
                title: 'You payment is pending',
                content: 'You can pay again from my items',
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search]);

    const saveSession = async (params) => {
        try {
            await onPaid({
                id: params.id,
                session: params.session,
                price: Number(params.price * 100),
                session_id: params.session_id,
            });

            modal.success({
                title: 'Transaction completed successfully',
                content: 'Thank you for your payment',
                onOk: () => {
                    navigate('/sos/my-sos', { replace: true });
                    modal.confirm({
                        ...donatePopupFields,
                    });
                },
            });
        } catch (e) {
            console.error(e);
            navigate('/sos/my-sos', { replace: true });
        }
    };

    const [checkoutSos] = useSosNewContainerCheckoutSosMutation({
        onError: (error) => message.error(error.message),
    });
    const DeleteButton = ({ sos }) => (
        <Button
            danger
            icon={<DeleteOutlined />}
            onClick={() => {
                modal.confirm({
                    title: 'Confirm',
                    icon: <ExclamationCircleOutlined />,
                    content: (
                        <>
                            Do you want to delete this SOS request?
                            <div>
                                "<i>{sos.message}</i>"
                            </div>
                        </>
                    ),
                    cancelButtonProps: {
                        danger: true,
                    },
                    onOk() {
                        onDelete(sos.id);
                    },
                });
            }}
            type="link"
        >
            Delete
        </Button>
    );

    const PayNowButton = ({ sos }) => {
        return (
            <Button
                className="payNowBtn"
                onClick={async () => {
                    const checkoutResponse = await checkoutSos({
                        variables: {
                            id: sos.id,
                        },
                    });
                    if (checkoutResponse && checkoutResponse.data && checkoutResponse.data.checkoutSos) {
                        window.open(checkoutResponse.data.checkoutSos, '_self');
                    }
                }}
                type="link"
            >
                Pay Now
            </Button>
        );
    };

    const handleAskRefundClick = (id) => {
        modal.confirm({
            title: 'Ask for Refund',
            icon: <ExclamationCircleOutlined />,
            content: (
                <div>
                    Are you sure you want to ask for the refund? You can always update the SOS request and resubmit for approval.
                    <br />
                    <br />
                    <small>
                        <i>If you wish to ask for refund, The refund will be processed within 5-7 business days if applicable.</i>
                    </small>
                </div>
            ),
            okText: 'Proceed',
            cancelButtonProps: {
                danger: true,
            },
            onOk() {
                onRequestRefund(id);
            },
        });
    };
    const getReviewActionButtons = (sos) => {
        const buttons = [<DeleteButton sos={sos} />];
        if (!sos.isItemPaid) {
            buttons.push(<PayNowButton sos={sos} />);
        }
        return buttons;
    };

    const getAttentionRequiredActionButtons = (sos) => {
        const buttons = [
            <Link to={`/sos/${sos.id}/edit`}>
                <Button type="link">Edit & Re-submit</Button>
            </Link>,
            <DeleteButton sos={sos} />,
        ];

        if (sos.isItemPaid) {
            buttons.push(
                <Button
                    onClick={() => {
                        handleAskRefundClick(sos.id);
                    }}
                    style={{ backgroundColor: 'red' }}
                    type="primary"
                >
                    Ask for Refund
                </Button>
            );
        }

        return buttons;
    };

    return (
        <SosMySosLayout onBack={onBack}>
            <SosSection data={data.me.mySos.filter((sos) => sos.status === SosStatus.Pending)} emptyText="You Have No SOS Awaiting Review" icon={<ClockCircleOutlined style={{ color: '#7fc1c6' }} />} renderItem={(sos) => <SosItem {...sos} actions={getReviewActionButtons(sos)} />} title="Awaiting Review" />

            <SosSection data={data.me.mySos.filter((sos) => sos.status === SosStatus.Rejected)} emptyText="You Have No SOS That Require Attention" icon={<WarningOutlined style={{ color: '#f97f48' }} />} renderItem={(sos) => <SosItem {...sos} actions={getAttentionRequiredActionButtons(sos)} />} title="Attention Required" />

            <SosSection data={data.me.mySos.filter((sos) => sos.status === SosStatus.Approved)} emptyText="You Have Not Sent Any SOS" icon={<CheckCircleOutlined style={{ color: '#adc965' }} />} renderItem={(sos) => <SosItem {...sos} />} title="Sent SOS" />

            <SosSection data={data.me.mySos.filter((sos) => sos.status === SosStatus.RefundRequested)} emptyText="No SOS found" icon={<WarningOutlined style={{ color: '#f97f48' }} />} renderItem={(sos) => <SosItem {...sos} actions={[]} />} title="Refund Requested" />

            <SosSection data={data.me.mySos.filter((sos) => sos.status === SosStatus.Refunded)} emptyText="No SOS found" icon={<WarningOutlined style={{ color: '#f97f48' }} />} renderItem={(sos) => <SosItem {...sos} actions={[]} />} title="Refunded" />
        </SosMySosLayout>
    );
};

SosMySosData.propTypes = {
    data: shape({
        me: shape({
            id: string.isRequired,
            sos: arrayOf(
                shape({
                    id: string.isRequired,
                })
            ),
        }),
    }),
    onBack: func.isRequired,
    onDelete: func.isRequired,
    onRequestRefund: func.isRequired,
};

const SosMySosView = {
    Error: ({ onBack, ...props }) => (
        <SosMySosLayout onBack={onBack}>
            <PageError {...props} />
        </SosMySosLayout>
    ),
    Data: SosMySosData,
    Loading: ({ onBack }) => (
        <SosMySosLayout onBack={onBack}>
            <PageLoading />
        </SosMySosLayout>
    ),
};

export { SosMySosView };
